"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"

interface PaymentModalProps {
  total: number
  descuentoInicial: number
  isOpen: boolean
  onClose: () => void
  onSubmit: (paymentMethod: string, receivedAmount: number, discount: number) => void
}

const PaymentModal: React.FC<PaymentModalProps> = ({ total, descuentoInicial, isOpen, onClose, onSubmit }) => {
  const [paymentMethod, setPaymentMethod] = useState<string>("efectivo")
  const [receivedAmount, setReceivedAmount] = useState<string>("")
  const [discount, setDiscount] = useState<string>(descuentoInicial.toString())
  const [change, setChange] = useState<number>(0)

  const receivedAmountInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isOpen) {
      // Limpiar valores de descuento y cantidad recibida cuando se cierra el modal
      setDiscount("")
      setReceivedAmount("")
      setChange(0) // Restablecer el cambio
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && receivedAmountInputRef.current) {
      receivedAmountInputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const subtotalConDescuento = total * (1 - (Number.parseFloat(discount) || 0) / 100)
      if (e.key === "Escape") {
        onClose()
      } else if (e.key === "F8") {
        e.preventDefault()
        if (receivedAmountInputRef.current) {
          receivedAmountInputRef.current.focus()
        }
      } else if (e.shiftKey && Number.parseFloat(receivedAmount) >= subtotalConDescuento) {
        onSubmit(paymentMethod, Number.parseFloat(receivedAmount), Number.parseFloat(discount))
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown)
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen, receivedAmount, discount, total, paymentMethod, onClose, onSubmit])

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener("keydown", handleEscapeKey)
    }

    return () => {
      window.removeEventListener("keydown", handleEscapeKey)
    }
  }, [isOpen, onClose])

  // Definir la función antes de su uso
  const getRecommendedAmounts = (total: number, discount: number) => {
    const totalConDescuento = Math.round(total * (1 - discount / 100))
    const recommendations = [totalConDescuento] // El primero debe ser exacto
  
    let amount = Math.ceil(totalConDescuento / 1000) * 1000
  
    if (amount < 50000) {
      amount = Math.ceil(amount / 5000) * 5000
    } else {
      amount = Math.ceil(amount / 10000) * 10000
    }
  
    recommendations.push(amount)
  
    const increments = amount < 50000 ? 10000 : 50000
    for (let i = 2; i < 6; i++) {
      amount += increments
      recommendations.push(amount)
    }
  
    return recommendations.slice(0, 6)
  }  

  const recommendedAmounts = getRecommendedAmounts(total, Number.parseFloat(discount) || 0)

  const handleReceivedAmountChange = (value: string) => {
    if (value === "") {
      setReceivedAmount("")
      setChange(0)
    } else {
      const amount = Number.parseFloat(value)
      setReceivedAmount(value)
      setChange(amount - total * (1 - (Number.parseFloat(discount) || 0) / 100))
    }
  }

  const handleDiscountChange = (value: string) => {
    if (value === "") {
      setDiscount("")
      setChange((Number.parseFloat(receivedAmount) || 0) - total)
    } else {
      const discountValue = Math.max(0, Math.min(Number.parseFloat(value), 100))
      setDiscount(discountValue.toString())
      setChange((Number.parseFloat(receivedAmount) || 0) - total * (1 - discountValue / 100))
    }
  }

  const handlePaymentMethodChange = (method: string) => {
    setPaymentMethod(method)
  }

  const handleFormSubmit = () => {
    const subtotalConDescuento = total * (1 - (Number.parseFloat(discount) || 0) / 100)
    if (Number.parseFloat(receivedAmount) >= subtotalConDescuento) {
      onSubmit(paymentMethod, Number.parseFloat(receivedAmount), Number.parseFloat(discount))
      onClose()
    } else {
      alert("La cantidad recibida debe ser igual o mayor al total con descuento.")
    }
  }

  if (!isOpen) return null

  const subtotalConDescuento = total * (1 - (Number.parseFloat(discount) || 0) / 100)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col transform transition-all">
        {/* Cabecera */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-600 p-5 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Finalizar Venta</h2>
              <h3 className="text-lg opacity-90">Subtotal: ${total.toLocaleString()}</h3>
              <h3 className="text-lg font-bold">Total con Descuento: ${subtotalConDescuento.toLocaleString()}</h3>
            </div>
            <div className="bg-blue-800/40 p-3 rounded-xl backdrop-blur-sm">
              <h3 className="text-lg">Cambio:</h3>
              <h3 className="text-2xl font-bold">${change >= 0 ? change.toLocaleString() : "0"}</h3>
            </div>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="flex-grow overflow-y-auto p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Columna Izquierda */}
          <div className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="discount" className="block text-sm font-medium text-gray-700">
                Descuento Completo (%)
              </label>
              <input
                type="text"
                id="discount"
                value={discount}
                onChange={(e) => handleDiscountChange(e.target.value)}
                placeholder="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="receivedAmount" className="block text-sm font-medium text-gray-700">
                Cantidad Recibida:
              </label>
              <input
                type="text"
                id="receivedAmount"
                value={receivedAmount}
                onChange={(e) => handleReceivedAmountChange(e.target.value)}
                placeholder="0"
                ref={receivedAmountInputRef}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Opciones rápidas:</p>
              <div className="grid grid-cols-3 gap-2">
                {recommendedAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleReceivedAmountChange(amount.toString())}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg 
                             transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    ${amount.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-800">Método de Pago</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handlePaymentMethodChange("efectivo")}
                className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                          ${
                            paymentMethod === "efectivo"
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                          }`}
              >
                <i className="fas fa-money-bill-wave text-2xl mb-2"></i>
                <span className="font-medium">Efectivo</span>
              </button>

              <button
                onClick={() => handlePaymentMethodChange("tarjeta")}
                className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                          ${
                            paymentMethod === "tarjeta"
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                          }`}
              >
                <i className="fas fa-credit-card text-2xl mb-2"></i>
                <span className="font-medium">Tarjeta</span>
              </button>

              <button
                onClick={() => handlePaymentMethodChange("transferencia")}
                className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                          ${
                            paymentMethod === "transferencia"
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                          }`}
              >
                <i className="fas fa-university text-2xl mb-2"></i>
                <span className="font-medium">Transferencia</span>
              </button>

              <button
                onClick={() => handlePaymentMethodChange("nequi")}
                className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                          ${
                            paymentMethod === "nequi"
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                          }`}
              >
                <div className="w-8 h-8 flex items-center justify-center mb-2">
                  <img src="/icons/nequi.png" alt="Nequi" className="w-full h-full object-contain" />
                </div>
                <span className="font-medium">Nequi</span>
              </button>
            </div>
          </div>
        </div>

        {/* Pie de página */}
        <div className="p-5 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 
                     transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            <i className="fas fa-times-circle mr-2"></i>
            Cancelar
          </button>

          <button
            onClick={handleFormSubmit}
            className={`px-5 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 
                      ${
                        Number.parseFloat(receivedAmount) >= subtotalConDescuento
                          ? "bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 focus:ring-green-500"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed"
                      }`}
            disabled={Number.parseFloat(receivedAmount) < subtotalConDescuento}
          >
            <i className="fa-solid fa-money-bill-transfer mr-2"></i>
            Facturar
          </button>
        </div>

        {/* Guía de atajos */}
        <div className="px-5 py-2 bg-gray-100 border-t border-gray-200 flex justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">F8</span>
            <span className="text-sm text-gray-600">Foco</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">ESC</span>
            <span className="text-sm text-gray-600">Salir</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">SHIFT</span>
            <span className="text-sm text-gray-600">Facturar</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentModal
